import React, { useEffect, useState } from "react";
// Components
import { TableBody, TableHead } from "./components";
// Hooks
import useActions from "hooks/useActions";
// Store Utils
import { programsSortingAction } from "store/filters";
import { useDispatch } from "react-redux";
// Utils
import exportDataToCsv from "utils/exportDataToCsv";
import getAllRowsData from "utils/getAllRowsData";
// Exteranl Tools
import { snakeCase } from "change-case";
// types
import { TableProps } from "./types";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import { filterKeys } from "Components/Users/Table";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { ICommunity } from "store/communityProfile";
import { useParams } from "react-router-dom";
import { getParentEntity } from "utils/getParentEntitywithParams";

export const KEYS_TO_BE_REMOVED = ["id"];
const generatingDirectoryActions = (
  columns: any,
  rowsData: any,
  parentEntity: any
) => {
  const fileDownload = async () => {
    if (rowsData.length === 0) {
      Toasts.error("Please select at least one program from the table");
      return;
    }

    try {
      const res = await apiLibrary.file.fileDownload(
        "programs",
        columns,
        rowsData.map((item: any) => {
          return item.programName.programId;
        }),
        parentEntity
      );

      // Convert the response to a Blob
      const blob = new Blob([res], { type: "application/octet-stream" });

      // Create a link element
      const link = document.createElement("a");

      // Set the href attribute to a URL created from the Blob
      link.href = window.URL.createObjectURL(blob);

      // Set the download attribute to specify the file name
      link.download = `programs_${new Date().toISOString()}.csv`;

      // Append the link to the body
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);

      Toasts.success("Download complete");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    }
  };

  return [
    {
      label: "Export Details",
      onClick: () => fileDownload(),
      divider: false,
      isVisible: true,
    },
  ];
};

export const Table: React.FC<TableProps> = ({
  table,
  columnOrder,
  columns,
  columnVisibility,
  flexRender,
  setSorting,
  sorting,
}) => {
  interface ParentEntity {
    label: string;
    value: string;
  }

  const dispatch = useDispatch();
  const { programsActions } = useActions();
  const { communityId, organizationId } = useParams();
  const [parentEntity, setParentEntity] = useState<ParentEntity | null>(null);

  useEffect(() => {
    const entity = getParentEntity({ communityId, organizationId });
    if (entity) {
      setParentEntity(entity);
    }
  }, [communityId, organizationId]);

  const DirectoryActions = () => {
    const filteredKeys = filterKeys(columnVisibility, columnOrder);
    return generatingDirectoryActions(
      filteredKeys,
      getAllRowsData(table, columnOrder, columnVisibility, columns),
      parentEntity
    );
  };

  const getProgramsActions = (data: any) => {
    const modalData = {
      status: data?.programName?.status,
      id: data?.programName?.programId,
      name: data?.programName?.programName,
      programAdmins: [],
      profileImage: data.programName?.image?.file?.thumb,
      path: "directory",
      openAccess: data?.openAccess,
      deletePermission: data?.deletePermission,
      code: data.programCode,
      mapType: data?.mapType
    };

    return programsActions(modalData);
  };

  const toggleSorting = (columnId: string) => {
    setSorting((currentSorting: any) => {
      const existingSort = currentSorting.find(
        (sort: any) => sort.columnId === columnId
      );
      const columnsId = snakeCase(columnId);
      if (!existingSort) {
        dispatch(programsSortingAction(columnsId, "asc"));

        return [{ columnId, type: "asc" }];
      } else if (existingSort.type === "asc") {
        dispatch(programsSortingAction(columnsId, "desc"));

        return [{ columnId, type: "desc" }];
      } else {
        dispatch(programsSortingAction("", ""));

        return [];
      }
    });
  };
  const handleClickOnTableColumnHeading = (header: any) =>
    header.column.getCanSort() && toggleSorting(header.column.id);
  return (
    <div className="w-full overflow-hidden overflow-x-scroll max-h-[80vh] overflow-y-auto">
      <table className="w-full">
        <TableHead
          table={table}
          sorting={sorting}
          flexRender={flexRender}
          DirectoryActions={DirectoryActions()}
          handleClickOnColumnHead={handleClickOnTableColumnHeading}
        />
        <TableBody
          flexRender={flexRender}
          table={table}
          columnVisibility={columnVisibility}
          getProgramsActions={getProgramsActions}
        />
      </table>
    </div>
  );
};
